/**
 * TripContent.js
 *
 * Content section for trip cards.
 * Displays trip-specific information like dates, duration, travelers, activities.
 */

import React, { memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { parseLocalDate, getDisplayLocale } from '../../../utils/dateUtils';
import Icon from '../../shared/Icon';
import ProvenanceLine from '../../ProvenanceLine';

/**
 * Format date range for display
 */
const formatDateRange = (startDate, endDate) => {
  if (!startDate) return null;

  const start = parseLocalDate(startDate);
  const end = endDate ? parseLocalDate(endDate) : null;

  const formatOptions = { month: 'short', day: 'numeric' };

  if (!end || start.getTime() === end.getTime()) {
    return start.toLocaleDateString(getDisplayLocale(), formatOptions);
  }

  const startMonth = start.toLocaleDateString(getDisplayLocale(), { month: 'short' });
  const endMonth = end.toLocaleDateString(getDisplayLocale(), { month: 'short' });

  if (startMonth === endMonth) {
    return `${startMonth} ${start.getDate()}-${end.getDate()}`;
  }

  return `${start.toLocaleDateString(getDisplayLocale(), formatOptions)} - ${end.toLocaleDateString(getDisplayLocale(), formatOptions)}`;
};

/**
 * Status badge
 */
const StatusBadge = ({ status }) => {
  const { t } = useTranslation('feedCard');

  // The map is the allowlist. `planning` is the creation default and says
  // nothing, so it stays out — that matches the old render gate, which already
  // excluded it. Everything else that the data can actually hold stays IN:
  // `completed` is the card's ONLY "this already happened" signal, because
  // formatDateRange below emits no year ("Aug 12-17" reads the same for a trip
  // three weeks past and one eleven months out) and CardBadges renders null for
  // trips. `cancelled` is not written by any shipped UI today, but PUT
  // /api/trips/:id passes status through unvalidated and the backend reads
  // 'cancelled' in several places, so it stays as the forward-compatible case.
  // `ongoing` is omitted deliberately: nothing ever writes it, and the render
  // gate below suppresses the chip for exactly the dates that would justify it.
  // The saving that mattered came from moving the chip INTO the info row
  // (below), not from deleting statuses.
  const config = {
    completed: { labelKey: 'tripContent.status.completed', bgClass: 'bg-background', textClass: 'text-content-primary' },
    cancelled: { labelKey: 'tripContent.status.cancelled', bgClass: 'bg-error-light', textClass: 'text-error' }
  };

  const entry = config[status];
  if (!entry) return null;
  const { labelKey, bgClass, textClass } = entry;

  return (
    <span className={`px-2 py-0.5 ${bgClass} ${textClass} text-xs rounded-full`}>
      {t(labelKey)}
    </span>
  );
};

/**
 * Main TripContent Component
 *
 * Props:
 * @param {Object} data - Normalized card data
 */
const TripContent = ({ data }) => {
  const { t } = useTranslation('feedCard');
  const navigate = useNavigate();
  const { title, description, location, typeData, detailUrl } = data;
  const {
    startDate,
    endDate,
    durationDays,
    travelerCount = 0,
    activityCount = 0,
    status,
    myFeedbackCompleted = false,
    copyCount = 0,
    friendsCopyCount = 0,
    provenanceSummary = null
  } = typeData || {};

  const dateDisplay = formatDateRange(startDate, endDate);

  // Determine if the trip is happening today and compute "Day X", or if the
  // trip ended within the last 7 days and the user hasn't reviewed yet.
  // Compare by calendar date via UTC epoch of local Y-M-D so we ignore time-of-day
  // (parseLocalDate anchors to local noon) and stay DST-safe.
  const { activeDayInfo, recapPrompt } = (() => {
    if (!startDate || !endDate) return { activeDayInfo: null, recapPrompt: false };
    const now = new Date();
    const start = parseLocalDate(startDate);
    const end = parseLocalDate(endDate);
    const todayUTC = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
    const startUTC = Date.UTC(start.getFullYear(), start.getMonth(), start.getDate());
    const endUTC = Date.UTC(end.getFullYear(), end.getMonth(), end.getDate());
    if (todayUTC >= startUTC && todayUTC <= endUTC) {
      return { activeDayInfo: { dayNum: Math.round((todayUTC - startUTC) / 86400000) + 1 }, recapPrompt: false };
    }
    const daysSinceEnd = Math.floor((todayUTC - endUTC) / 86400000);
    if (daysSinceEnd > 0 && daysSinceEnd <= 7 && !myFeedbackCompleted) {
      return { activeDayInfo: null, recapPrompt: true };
    }
    return { activeDayInfo: null, recapPrompt: false };
  })();

  return (
    <div className="flex-grow">
      {/* Title */}
      <h3 className="font-semibold text-content-primary line-clamp-2 mb-2">
        {title || t('tripContent.titleFallback')}
      </h3>

      {/* Location */}
      {location && (
        <p className="text-sm text-content-secondary mb-2 line-clamp-1">
          <Icon name="mapPin" className="w-3.5 h-3.5 inline mr-1" />{location}
        </p>
      )}

      {/* Trip info row. The status chip rides along at the end rather than
          owning a row of its own — see StatusBadge for which statuses survive. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-primary font-medium mb-2">
        {/* Duration only without a date range: "Aug 12-17" already says "6 days",
            and at 288px the two together wrapped this row onto a second line. */}
        {!dateDisplay && (
          <span className="flex items-center">
            <Icon name="airplane" className="w-3 h-3 mr-1" />
            {durationDays
              ? t('tripContent.duration', { count: durationDays })
              : t('tripContent.durationFallback')}
          </span>
        )}
        {dateDisplay && (
          <span className="flex items-center">
            <Icon name="calendar" className="w-3 h-3 mr-1" />
            {dateDisplay}
          </span>
        )}
        {travelerCount > 0 && (
          <span className="flex items-center">
            <Icon name="userGroup" className="w-3 h-3 mr-1" />
            {t('tripContent.travelerCount', { count: travelerCount })}
          </span>
        )}
        {activityCount > 0 && (
          <span className="flex items-center">
            <Icon name="mapPin" className="w-3 h-3 mr-1" />
            {t('tripContent.activityCount', { count: activityCount })}
          </span>
        )}
        {friendsCopyCount > 0 ? (
          <span
            className="flex items-center"
            title={t('tripContent.friendsCopy.title', { count: friendsCopyCount })}
          >
            <Icon name="userGroup" className="w-3 h-3 mr-1 text-primary" />
            {t('tripContent.friendsCopy.body', { count: friendsCopyCount })}
          </span>
        ) : copyCount > 0 && (
          <span
            className="flex items-center text-content-muted"
            title={t('tripContent.inspiredCopy.title', { count: copyCount })}
          >
            <Icon name="sparkles" className="w-3 h-3 mr-1" />
            {t('tripContent.inspiredCopy.body', { count: copyCount })}
          </span>
        )}
        {(!activeDayInfo || status === 'cancelled') && <StatusBadge status={status} />}
      </div>

      {/* "Inspired by" provenance — credits source trips this was built from.
          Feed cards carry only the summary; the "& N others" modal fetches the
          full source list on open via data.id. */}
      {provenanceSummary && provenanceSummary.total > 0 && (
        <ProvenanceLine summary={provenanceSummary} tripId={data.id} className="mb-2" />
      )}

      {/* "View Day X" button — only for own trips happening today, not cancelled */}
      {activeDayInfo && detailUrl && data.creator?.isOwn && status !== 'cancelled' && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            navigate(`${detailUrl}?tab=map`);
          }}
          className="w-full mb-2 py-2 px-3 text-sm font-semibold rounded-lg bg-primary text-content-inverse hover:bg-primary-dark transition-colors flex items-center justify-center gap-2"
        >
          <Icon name="map" className="w-4 h-4" />
          {t('tripContent.viewDayButton', { day: activeDayInfo.dayNum })}
        </button>
      )}

      {/* "Share your trip" — recently-ended own trips that haven't been reviewed */}
      {recapPrompt && detailUrl && data.creator?.isOwn && status !== 'cancelled' && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            navigate(`${detailUrl}?tab=recap`);
          }}
          className="w-full mb-2 py-2 px-3 text-sm font-semibold rounded-lg bg-primary text-content-inverse hover:bg-primary-dark transition-colors flex items-center justify-center gap-2"
        >
          <Icon name="megaphone" className="w-4 h-4" />
          {t('tripContent.shareTripButton')}
        </button>
      )}

      {/* Description */}
      {description && (
        <p className="text-xs text-content-muted mb-2 line-clamp-2">
          {description}
        </p>
      )}
    </div>
  );
};

export default memo(TripContent);
